<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Wordmark header -->
      <header class="header">
        <div class="header-logo">
          <img :src="lightLogoUrl" class="logo" />
        </div>
        <div class="header-partnership">
          <span class="hp-lo">{{ loName || "Loan Officer" }}</span>
          <span class="hp-amp">&amp;</span>
          <span class="hp-re">{{ realtorName || realtor?.partner_title || 'Partner' }}</span>
        </div>
     
      </header>

      <!-- Thin rule -->
      <div class="page-rule"></div>

      <!-- Centered hero -->
      <section class="hero">
   
        <h1 class="hero-h1" v-html="heroH1Html"></h1>
        <p class="hero-body">{{ heroBody }}</p>
        <div class="hero-actions">
          <a class="act-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
          <a v-if="applyLeftLink" class="act-secondary" :href="applyLeftLink">{{ heroSecondaryText }}</a>
        </div>
        <div class="hero-micro">
          <a v-if="loinfo && loinfo.phone_number" class="micro-link" :href="'tel:' + loinfo.phone_number">Call</a>
          <span class="micro-sep">·</span>
          <a v-if="loinfo && loinfo.email" class="micro-link" :href="'mailto:' + loinfo.email">Email</a>
          <span v-if="realtor && realtor.website_url" class="micro-sep">·</span>
          <a v-if="realtor && realtor.website_url" class="micro-link" :href="realtor.website_url" target="_blank" rel="noreferrer">Partner site</a>
        </div>
      </section>

      <!-- Ornamental rule -->
      <div class="orn-rule">
        <div class="or-line"></div>
        <div class="or-dot"></div>
        <div class="or-line"></div>
      </div>

      <!-- Profile duo — centered horizontal -->
      <section class="profiles">

        <div class="pfolio lo-pfolio">
          <div class="pf-photo-wrap">
            <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="pf-photo" />
          </div>
          <div class="pf-tag">Loan Officer</div>
          <div class="pf-name">{{ loName }}</div>
          <div class="pf-role">{{ loTitle }}</div>
          <div v-if="loinfo && loinfo.nmls" class="pf-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
          <div class="pf-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="pf-link">{{ loinfo.phone_number }}</a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="pf-link">{{ loinfo.email }}</a>
          </div>
       
        </div>

        <div class="profiles-vdivider">
          <div class="vd-line"></div>
          <div class="vd-badge">×</div>
          <div class="vd-line"></div>
        </div>

        <div class="pfolio re-pfolio">
          <div class="pf-photo-wrap">
            <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="pf-photo" />
          </div>
          <div class="pf-tag re-pf-tag">{{ realtor?.partner_title || 'Partner' }}</div>
          <div class="pf-name">{{ realtorName }}</div>
          <div class="pf-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
          <div v-if="realtor && realtor.nmls" class="pf-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
          <div class="pf-contacts">
            <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="pf-link re-pf-link">{{ realtor.phone_number }}</a>
            <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="pf-link re-pf-link">{{ realtor.email }}</a>
            <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="pf-link re-pf-link" target="_blank" rel="noreferrer">Website</a>
          </div>
        </div>

      </section>

      <!-- Footer -->
      <footer class="footer">
        <div class="footer-inner">
          <span class="footer-copy">{{ loName }} × {{ realtorName }}</span>
          <a class="footer-apply" :href="applyRightLink">{{ heroPrimaryText }} →</a>
        </div>
      </footer>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandFolio",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    lightLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #fdfaf6;
  color: #2a2318;
  font-family: 'Source Sans 3', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --hairline: rgba(42,35,24,0.10);
  --muted: #7a6e62;
  --a-soft: color-mix(in srgb, var(--a) 12%, #fdfaf6);
  --p-soft: color-mix(in srgb, var(--p) 5%, #fdfaf6);
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* HEADER */
.header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 60px;
  border-bottom: 1px solid var(--hairline);
}
.logo { width: 160px; max-height:80px; object-fit: contain; }
.header-partnership { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.hp-lo { font-weight: 600; color: var(--p); }
.hp-amp { color: var(--a); font-size: 16px; }
.hp-re { color: var(--muted); }
.header-apply {
  font-size: 13px; font-weight: 600; text-decoration: none;
  color: var(--p); border: 1.5px solid color-mix(in srgb, var(--p) 25%, transparent);
  padding: 9px 20px; border-radius: 6px; transition: all 0.18s;
}
.header-apply:hover { background: var(--p); color: #fff; }

.page-rule { height: 3px; background: linear-gradient(90deg, var(--p), var(--a), var(--p)); opacity: 0.15; }

/* HERO — centered */
.hero { text-align: center; padding: 135px 24px 60px; max-width: 720px; margin: 0 auto; }
.hero-kicker { font-size: 11px; font-weight: 600; letter-spacing: 0.24em; text-transform: uppercase; color: var(--a); margin-bottom: 18px; }
.hero-h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(38px, 5.5vw, 72px);
  font-weight: 700; line-height: 1.08;
  color: var(--p); margin: 0 0 20px;
}
.hero-body { font-size: 16px; line-height: 1.78; color: var(--muted); margin-bottom: 32px; }
.hero-actions { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; justify-content: center; }
.act-primary {
  font-size: 15px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 14px 32px; border-radius: 8px;
  box-shadow: 0 4px 18px color-mix(in srgb, var(--p) 22%, transparent);
  transition: opacity 0.2s;
  width:55%
 
}
.act-primary:hover { opacity: 0.87; }
.act-secondary {
  font-size: 15px; font-weight: 400; text-decoration: none;
  color: var(--muted); border: 1.5px solid var(--hairline);
  padding: 14px 32px; border-radius: 8px; transition: all 0.18s;
}
.act-secondary:hover { border-color: color-mix(in srgb, var(--p) 22%, transparent); color: var(--p); }
.hero-micro { display: flex; justify-content: center; align-items: center; gap: 8px; }
.micro-link { font-size: 13px; text-decoration: none; color: var(--muted); border-bottom: 1px solid var(--hairline); padding-bottom: 1px; transition: color 0.18s; }
.micro-link:hover { color: var(--p); border-color: var(--p); }
.micro-sep { color: var(--hairline); }

/* ORNAMENTAL RULE */
.orn-rule { display: flex; align-items: center; gap: 14px; max-width: 600px; margin: 0 auto; padding: 0 24px; }
.or-line { flex: 1; height: 1px; background: var(--hairline); }
.or-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--a); flex-shrink: 0; }

/* PROFILES */
.profiles {
  display: flex; align-items: flex-start; justify-content: center;
  gap: 0; padding: 64px 40px 80px;
  max-width: 900px; margin: 0 auto; width: 100%;
}
.pfolio { flex: 1; text-align: center; padding: 0 40px; }
.pf-photo-wrap { margin-bottom: 20px; }
.pf-photo {
  width: 96px; height: 96px; border-radius: 50%; object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 8px 28px rgba(0,0,0,0.12);
  display: inline-block;
}
.pf-tag { font-size: 10px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 8px; }
.re-pf-tag { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.pf-name { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 700; color: var(--p); margin-bottom: 4px; }
.pf-role { font-size: 13px; color: var(--muted); margin-bottom: 4px; }
.pf-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 60%, transparent); margin-bottom: 16px; }
.pf-contacts { display: flex; flex-direction: column; align-items: center; gap: 5px; margin-bottom: 18px; }
.pf-link { font-size: 12px; text-decoration: none; color: var(--muted); border-bottom: 1px solid var(--hairline); padding-bottom: 1px; transition: color 0.18s; }
.pf-link:hover { color: var(--p); }
.re-pf-link:hover { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.pf-cta {
  display: inline-block; text-decoration: none;
  background: var(--p); color: #fff;
  font-size: 13px; font-weight: 600; padding: 10px 22px; border-radius: 8px;
  transition: opacity 0.18s;
}
.pf-cta:hover { opacity: 0.85; }

/* Vertical divider */
.profiles-vdivider {
  width: 1px; display: flex; flex-direction: column; align-items: center;
  padding: 20px 0; flex-shrink: 0; margin-top: 36px;
}
.vd-line { flex: 1; width: 1px; background: var(--hairline); min-height: 40px; }
.vd-badge { font-size: 18px; color: var(--a); margin: 8px 0; font-weight: 300; }

/* FOOTER */
.footer { border-top: 1px solid var(--hairline); background: var(--p-soft); margin-top: auto; }
.footer-inner { display: flex; align-items: center; justify-content: space-between; padding: 20px 60px; }
.footer-copy { font-size: 13px; color: var(--muted); }
.footer-apply { font-size: 13px; font-weight: 600; text-decoration: none; color: var(--p); }
.footer-apply:hover { color: color-mix(in srgb, var(--a) 80%, var(--p)); }

@media (max-width: 700px) {
  .profiles { flex-direction: column; align-items: center; }
  .profiles-vdivider { flex-direction: row; width: 100%; height: 1px; padding: 0; margin: 8px 0; }
  .vd-line { height: 1px; width: auto; flex: 1; }
  .vd-badge { margin: 0 12px; }
  .header { padding: 18px 24px; }
  .header-partnership { display: none; }
  .footer-inner { padding: 16px 24px; }
}
</style>
